import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const milestones = [
  { year: '1992', title: 'Founded', desc: 'Manmohan Sharma establishes Spiritual Bhakti in New Delhi' },
  { year: '2000', title: 'Digital Shift', desc: 'Pioneering digital music distribution in India' },
  { year: '2010', title: 'YouTube Launch', desc: 'First YouTube channel crosses 1 million subscribers' },
  { year: '2020', title: '100M Milestone', desc: 'Combined network surpasses 100 million subscribers' },
  { year: '2024', title: 'Legacy Continues', desc: '100B+ views, 50+ YouTube awards, 70+ team members' },
];

export default function AboutSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setVisible(true);
    }, { threshold: 0.15 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="section-padding bg-[#0d0d0d] relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 rounded-full bg-[#d4af37]/3 blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 rounded-full bg-[#d4af37]/3 blur-3xl pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className={`text-center mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-3 mb-4">
            <span className="w-8 h-px bg-[#d4af37]"></span>
            <span className="text-[#d4af37] text-xs font-semibold tracking-[3px] uppercase">Our Story</span>
            <span className="w-8 h-px bg-[#d4af37]"></span>
          </div>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Three Decades of <span className="text-[#d4af37]">Musical Excellence</span>
          </h2>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            From a humble cassette label to India's most powerful digital music network — the Spiritual Bhakti story is one of passion, perseverance, and purpose.
          </p>
        </div>

        {/* Main content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-20">
          <div className={`transition-all duration-700 delay-100 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative">
              <img
                alt="Spiritual Bhakti founder Manmohan Sharma music studio"
                className="w-full h-[480px] object-cover object-top rounded-2xl"
                src="/images/Director Photo (Manmohan Sharma).png"
                  onError={(e) => ((e.target as HTMLImageElement).src = "/images/default.jpg")}              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
              <div className="absolute bottom-6 left-6 right-6">
                <div className="glass-card rounded-xl p-4">
                  <div className="text-[#d4af37] font-heading font-bold text-lg">Manmohan Sharma</div>
                  <div className="text-white/70 text-sm">Founder & Chairman, Spiritual Bhakti</div>
                </div>
              </div>
              {/* Gold border accent */}
              <div className="absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 border-[#d4af37] rounded-tl-xl"></div>
              <div className="absolute -bottom-3 -right-3 w-16 h-16 border-b-2 border-r-2 border-[#d4af37] rounded-br-xl"></div>
            </div>
          </div>

          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <h3 className="font-heading text-3xl font-bold text-white mb-6">
              Born from the Soul of <span className="text-[#d4af37]">Indian Folk Music</span>
            </h3>
            <div className="space-y-4 text-white/70 text-base leading-relaxed">
              <p>
                In 1992, visionary entrepreneur <strong className="text-white">Manmohan Sharma</strong> founded Spiritual Bhakti in New Delhi with a singular mission — to preserve, promote, and celebrate the rich tapestry of India's folk music traditions. What began as a modest cassette label specializing in <strong className="text-white">Bhojpuri, Maithili, and Awadhi</strong> folk songs quickly became the heartbeat of millions across North India.
              </p>
              <p>
                As technology evolved, so did Spiritual Bhakti. Embracing the digital revolution with foresight and courage, the company transformed from a regional cassette label into <strong className="text-white">India's most powerful digital music network</strong> — spanning 23+ YouTube channels, 100M+ subscribers, and over 100 billion views.
              </p>
              <p>
                Today, with a dedicated team of <strong className="text-white">70+ professionals</strong> — producers, directors, artists, and digital strategists — Spiritual Bhakti continues to be the definitive platform for authentic Indian music, from Hindi Devotional bhajans to contemporary folk fusion.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-8 mb-8">
              {[
                { value: '30+', label: 'Years of Legacy' },
                { value: '70+', label: 'Team Members' },
                { value: '29+', label: 'Signed Artists' },
                { value: '23+', label: 'YouTube Channels' },
              ].map((item) => (
                <div key={item.label} className="glass-card rounded-xl p-4 text-center">
                  <div className="text-2xl font-bold font-heading text-[#d4af37]">{item.value}</div>
                  <div className="text-white/60 text-sm mt-1">{item.label}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-black whitespace-nowrap btn-gold cursor-pointer transition-all hover:scale-105">
                Work With Us <i className="ri-arrow-right-line"></i>
              </Link>
              <Link to="/artists" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white whitespace-nowrap border border-[#d4af37]/40 hover:border-[#d4af37] hover:bg-[#d4af37]/10 transition-all cursor-pointer">
                Meet Our Artists <i className="ri-user-star-line"></i>
              </Link>
            </div>
          </div>
        </div>

        {/* Timeline */}
        <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="font-heading text-2xl font-bold text-white text-center mb-10">Our Journey</h3>
          <div className="relative">
            <div className="absolute top-5 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#d4af37]/40 to-transparent hidden md:block"></div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
              {milestones.map((m, i) => (
                <div key={m.year} className={`relative text-center transition-all duration-500 delay-${i * 100}`}>
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#d4af37] text-black font-bold text-xs mx-auto mb-4 relative z-10">
                    {i + 1}
                  </div>
                  <div className="text-[#d4af37] font-heading font-bold text-lg mb-1">{m.year}</div>
                  <div className="text-white font-semibold text-sm mb-2">{m.title}</div>
                  <div className="text-white/50 text-xs leading-relaxed">{m.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
